/* eslint-disable no-underscore-dangle */
/* eslint-disable vue/prop-name-casing */
import {
  defineComponent, ref, reactive, watch, computed, PropType, inject, provide,
} from 'vue'
import type { Options as OffsetOptions } from '@floating-ui/core/src/middleware/offset'
import Dropdown from './Dropdown'
import {
  Grid, GridItem, GridRow, MouseState,
} from '../Grid'
import { Divider } from '../Divider'
import { Icon, IconProps } from '../Icon'
import { Text } from '../Text'
import { Tooltip } from '../Popover'
import { Tag } from '../Tag'
import { Checkbox } from '../Checkbox'
import { offsetHelper } from '../Popover/utils'
import {
  dropdownProvision, DropdownProvision, optionProvision, OptionProvision,
} from './provision'
import { CheckSmall, Right } from '../../icons'

import { OptionContent, OptionDivider } from './interface'
import { Placement, Trigger, DropdownSize } from '../../enums'

export default defineComponent({
  inheritAttrs: false,
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: Function as PropType<(p: IconProps) => string>,
      default: undefined,
    },
    name: {
      type: String,
      default: undefined,
    },
    label: {
      type: String,
      default: undefined,
    },
    value: {
      type: [String, Number, Boolean] as PropType<(string | number | boolean)>,
      default: undefined,
    },
    extra: {
      type: Object,
      default: undefined,
    },
    tooltip: {
      type: String,
      default: undefined,
    },
    mark: {
      type: String,
      default: undefined,
    },
    /**
     * 下级 Dropdown 展示位置
     */
    placement: {
      type: String as PropType<Placement>,
      default: 'right-start',
    },
    /**
     * 下级 Dropdown 与目标元素的距离
     */
    offset: {
      type: [Number, Object, Function] as PropType<OffsetOptions>,
      default: offsetHelper(12, -9),
    },
    /**
     * 下级 Dropdown 触发方式
     */
    trigger: {
      type: String as PropType<Trigger>,
      default: undefined,
    },
    /**
     * 下级 Dropdown 是否展示
     */
    visible: {
      type: Boolean,
      default: false,
    },
    /**
     * 下级 Dropdown 触发方式
     */
    size: {
      type: String as PropType<DropdownSize>,
      default: undefined,
    },
    children: {
      type: Array as PropType<(OptionContent | OptionDivider)[]>,
      default: () => ([]),
    },
    divider: {
      type: [String, Boolean],
      default: undefined,
    },
    maxDropdownWidth: {
      type: [String, Number],
      default: undefined,
    },
    maxDropdownHeight: {
      type: [String, Number],
      default: undefined,
    },
    dropdownClass: {
      type: [String, Object],
      default: undefined,
    },
    dropdownStyle: {
      type: [String, Object],
      default: undefined,
    },
    id: {
      type: String,
      default: undefined,
    },
    virtualize: {
      type: Boolean,
      default: undefined,
    },
    virtualizeOptions: {
      type: Object as PropType<{ optionHeight?: number; optionGap?: number; renderLimit?: number; preCreate?: boolean }>,
      default: undefined,
    },
    /** @internal */
    _index: {
      type: Number,
      default: undefined,
    },
  },
  emits: {
    click(e: MouseEvent) {
      return e !== undefined
    },
    mousedown(e: MouseEvent) {
      return e !== undefined
    },
    mouseenter(e: MouseEvent) {
      return e !== undefined
    },
    mouseleave(e: MouseEvent) {
      return e !== undefined
    },
    mouseup(e: MouseEvent) {
      return e !== undefined
    },
  },
  setup(props, { slots, attrs, emit }) {
    if (props.divider) {
      return () => <GridItem row={`${props._index}`} column={'1 / -1'}>
        <Divider />
      </GridItem>
    }

    const label = computed(
      () => props.label || props.name,
    )

    const id = `${label.value}-${props.value}-${props.id}`

    provide<OptionProvision>(optionProvision, { id })

    const option = inject<OptionProvision | undefined>(optionProvision, undefined)
    const dropdown = inject<DropdownProvision | undefined>(dropdownProvision, undefined)

    const isLeaf = computed(
      () => !props.children.length && !slots.default,
    )

    const subOptions = ref<{ option: OptionContent; active: boolean; subActive: boolean }[]>()
    const subActiveOptions = ref<{ option: OptionContent; active: boolean; subActive: boolean }[]>()

    const active = ref(false)
    const subActive = ref(false)

    watch(
      () => dropdown?.modelValue.value,
      v => {
        if (label.value !== undefined && props.value !== undefined) {
          const isActive = Array.isArray(v)
            ? v.includes(props.value)
            : v === props.value

          if (active.value !== isActive) {
            active.value = isActive

            if (dropdown) {
              if (isActive && !(id in dropdown.activeOptions.value)) {
                dropdown.activeOptions.value = {
                  ...Array.isArray(v)
                    ? dropdown.activeOptions.value
                    : Object
                      .keys(dropdown.activeOptions.value)
                      .reduce(
                        (a: Record<string, OptionContent>, c) => {
                          const content = dropdown.activeOptions.value[c]

                          if (content.value === v) {
                            return {
                              ...a,
                              [c]: content,
                            }
                          }

                          return a
                        },
                        {},
                      ),
                  [id]: props,
                }
              }

              if (!isActive && id in dropdown.activeOptions.value) {
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { [id]: _, ...rest } = dropdown.activeOptions.value

                dropdown.activeOptions.value = rest
              }

              if (option?.id) {
                if (!dropdown.optionsDescription[option.id]) {
                  dropdown.optionsDescription[option.id] = {}
                }

                if (dropdown.optionsDescription[option.id][id]?.active !== active.value) {
                  dropdown.optionsDescription[option.id][id] = { option: props, active: active.value, subActive: subActive.value }
                }
              }
            }
          }
        }
      },
      {
        immediate: true,
      },
    )

    watch(
      () => dropdown?.optionsDescription,
      v => {
        subOptions.value = Object.values(v?.[id] || {})
        subActiveOptions.value = subOptions.value.filter(({ active: a, subActive: s }) => (a || s))

        if (subActiveOptions.value.length > 0) {
          subActive.value = true
        } else {
          subActive.value = false
        }

        if (dropdown && option?.id) {
          if (!dropdown.optionsDescription[option.id]) {
            dropdown.optionsDescription[option.id] = {}
          }

          if (dropdown.optionsDescription[option.id][id]?.subActive !== subActive.value) {
            dropdown.optionsDescription[option.id][id] = { option: props, active: active.value, subActive: subActive.value }
          }
        }
      },
      {
        immediate: true,
        deep: true,
      },
    )

    function onClick(e: MouseEvent) {
      // only valued options can be activated actively
      if (props.value !== undefined && dropdown !== undefined && !props.disabled) {
        if (dropdown.multiple.value) {
          // eslint-disable-next-line no-nested-ternary
          const temp = Array.isArray(dropdown.modelValue.value)
            ? dropdown.modelValue.value
            : dropdown.modelValue.value !== undefined
              ? [dropdown.modelValue.value]
              : []

          if (active.value) {
            dropdown.modelValue.value = temp.filter(x => x !== props.value)
          } else {
            dropdown.modelValue.value = Array
              .from(
                new Set([...temp, props.value]),
              )
          }
        } else if (active.value && dropdown.revertable.value) {
          dropdown.modelValue.value = undefined
        } else {
          dropdown.modelValue.value = props.value
        }
      }

      emit('click', e)
    }

    const mouseState = reactive<MouseState>({
      pressing: false,
      hover: false,
    })

    function onStateChange(
      { pressing, hover }: MouseState,
    ) {
      mouseState.pressing = pressing
      mouseState.hover = hover
    }

    function markText(text: string) {
      const mark = (props.mark || dropdown?.mark.value)

      if (mark && text) {
        if (text.indexOf(mark) > -1) {
          return <>
            {
              text
                .split(mark)
                .map(
                  (t, i) => {
                    if (i > 0) {
                      return <>
                        <mark class={['--color-bg-transparent', '--color-primary']}>
                          {mark}
                        </mark>
                        {t}
                      </>
                    }

                    return t
                  },
                )
            }
          </>
        }
      }

      return <>{text}</>
    }

    const nameText = ref()
    const subActiveText = ref()

    return () => {
      const optionSlot = slots.option || dropdown?.optionSlot.value
      const optionPrefixSlot = slots.optionPrefix || dropdown?.optionPrefixSlot.value
      const optionContentSlot = slots.optionContent || dropdown?.optionContentSlot.value
      const optionSuffixSlot = slots.optionSuffix || dropdown?.optionSuffixSlot.value

      if (dropdown?.dense.value) {
        return <div
          class={
            [
              'd-clickable',
              props.disabled && 'disabled',
              mouseState.pressing && 'pressing',
              mouseState.hover && 'hover',
            ]
          }
          {...{
            onClick,
            onMousedown: (e: MouseEvent) => {
              emit('mousedown', e)
              mouseState.pressing = true
            },
            onMouseenter: (e: MouseEvent) => {
              emit('mouseenter', e)
              mouseState.hover = true
            },
            onMouseleave: (e: MouseEvent) => {
              emit('mouseleave', e)
              mouseState.hover = false
              mouseState.pressing = false
            },
            onMouseup: (e: MouseEvent) => {
              emit('mouseup', e)
              mouseState.pressing = false
            },
          }}
        >
          {
            optionSlot
              ? optionSlot(
                {
                  option: props,
                  markText,
                  active: active.value,
                  disabled: props.disabled,
                  pressing: mouseState.pressing,
                  hover: mouseState.hover,
                },
              )
              : <Tag
                class={
                  [
                    // eslint-disable-next-line no-nested-ternary
                    ...props.disabled
                      ? [
                        '--color-text-disabled',
                        '--color-bg-fill-light',
                      ]
                      : active.value
                        ? [
                          '--color-primary',
                          '--color-bg-primary-light',
                        ]
                        : [
                          '--color-text-paragraph',
                          '--color-bg-fill',
                        ],
                    '--color-static',
                    props.disabled && 'disabled',
                    mouseState.pressing && 'pressing',
                    mouseState.hover && 'hover',
                  ]
                }
                disabled={props.disabled}
                bold={false}
                custom={true}
                v-slots={{
                  default: () => markText(label.value),
                }}
              />
          }
        </div>
      }

      let row: string = `${props._index}`

      if (dropdown?.optionVirtualize.value) {
        const { virtualizeBefore, virtualizeAfter } = dropdown.optionVirtualize.value

        if (props._index < virtualizeBefore || props._index > virtualizeAfter) {
          return null
        }

        row = `${props._index - (virtualizeBefore - 1)}`
      }

      return <Dropdown
        disabled={props.disabled}
        placement={props.placement}
        offset={
          typeof props.offset === 'number'
            ? offsetHelper(props.offset, -9)
            : props.offset
        }
        trigger={props.trigger ?? dropdown?.trigger.value}
        visible={props.visible ?? dropdown?.visible.value}
        size={props.size ?? dropdown?.size.value}
        multiple={dropdown?.multiple.value}
        options={props.children}
        revertable={dropdown?.revertable.value}
        mark={props.mark ?? dropdown?.mark.value}
        maxWidth={props.maxDropdownWidth ?? dropdown?.maxWidth.value}
        maxHeight={props.maxDropdownHeight ?? dropdown?.maxHeight.value}
        class={props.dropdownClass}
        style={props.dropdownStyle}
        virtualize={props.virtualize ?? dropdown?.virtualize.value}
        virtualizeOptions={props.virtualizeOptions ?? dropdown?.virtualizeOptions.value}
        _root={false}
        v-slots={{
          default: () => (
            optionSlot
              ? <GridItem
                row={row}
                column={'1 / -1'}
                class={[
                  props.disabled && 'disabled',
                  mouseState.pressing && 'pressing',
                  mouseState.hover && 'hover',
                ]}
                {...{
                  onClick,
                  onMousedown: (e: MouseEvent) => emit('mousedown', e),
                  onMouseenter: (e: MouseEvent) => emit('mouseenter', e),
                  onMouseleave: (e: MouseEvent) => emit('mouseleave', e),
                  onMouseup: (e: MouseEvent) => emit('mouseup', e),
                  onStateChange,
                }}>
                {
                  optionSlot(
                    {
                      option: props,
                      markText,
                      active: active.value,
                      subActive: subActive.value,
                      subOptions: subOptions.value,
                      disabled: props.disabled,
                      pressing: mouseState.pressing,
                      hover: mouseState.hover,
                    },
                  )
                }
              </GridItem>
              : <GridRow
                row={row}
                items={
                  [
                    {
                      column: '1 / 2',
                    },
                    {
                      column: '2 / -1',
                    },
                  ]
                }
                {...{
                  onClick,
                  onMousedown: (e: MouseEvent) => emit('mousedown', e),
                  onMouseenter: (e: MouseEvent) => emit('mouseenter', e),
                  onMouseleave: (e: MouseEvent) => emit('mouseleave', e),
                  onMouseup: (e: MouseEvent) => emit('mouseup', e),
                  onStateChange,
                }}
                v-slots={{
                  handler: () => <div
                    class={[
                      'd-option',
                      'd-option-handler',
                      !props.disabled
                        ? '--color-bg-fill-light'
                        : 'disabled',
                      mouseState.pressing && 'pressing',
                      mouseState.hover && 'hover',
                    ]}
                  />,
                }}
              >
                {
                  optionPrefixSlot
                    ? optionPrefixSlot(
                      {
                        option: props,
                        markText,
                        active: active.value,
                        subActive: subActive.value,
                        subOptions: subOptions.value,
                        disabled: props.disabled,
                        pressing: mouseState.pressing,
                        hover: mouseState.hover,
                      },
                    )
                    : <div
                      class={
                        [
                          'd-option',
                          'd-option-prefix-icon',
                          ...dropdown.multiple.value
                            ? ['--space-p-small']
                            : [
                              '--space-p-top-small',
                              '--space-p-right-small',
                              '--space-p-bottom-small',
                              '--space-p-left-small',
                            ],
                          ...props.disabled
                            ? ['--color-text-disabled', 'disabled']
                            : ['--color-text-description'],
                        ]
                      }
                    >
                      {
                        // eslint-disable-next-line no-nested-ternary
                        dropdown.multiple.value && props.value !== undefined
                          ? <Checkbox checked={active.value} value={props.value}/>
                          : active.value
                            ? <Icon icon={CheckSmall}/>
                            : <Icon color={'current'} icon={props.icon}/>
                      }
                    </div>
                }
                <Grid
                  columns={
                    !isLeaf.value
                      ? 'minmax(0, 1fr) min-content min-content min-content'
                      : 'minmax(0, 1fr) min-content min-content'
                  }
                  class={
                    [
                      'd-option',
                      'd-option-content',
                      ...props.disabled
                        ? ['--color-text-disabled', 'disabled']
                        : ['--color-text-title'],
                      attrs.class,
                    ]
                  }
                  style={attrs.style}
                >
                  {
                    optionContentSlot
                      ? optionContentSlot(
                        {
                          option: props,
                          markText,
                          active: active.value,
                          subActive: subActive.value,
                          subOptions: subOptions.value,
                          disabled: props.disabled,
                          pressing: mouseState.pressing,
                          hover: mouseState.hover,
                        },
                      )
                      : <div class="d-option-description">
                        <div class="d-option-name">
                          <Text
                            ref={nameText}
                            color={'current'}
                            ellipsis
                            v-slots={{
                              default: () => markText(label.value),
                            }}
                          />
                        </div>
                        <Tooltip
                          target={nameText.value?.$el}
                          validate={
                            () => (
                              props.tooltip
                              || nameText.value?.isEllipsisActive()
                            )
                          }
                          v-slots={{
                            content: () => props.tooltip || label.value,
                          }}
                        />
                        {
                          subActive.value
                      && <>
                        <div class="d-option-sub-active-description">
                          <Text
                            ref={subActiveText}
                            type={'description'}
                            ellipsis
                            v-slots={{
                              default: () => markText(subActiveOptions.value.map(({ option: o }) => o.label || o.name).join(', ')),
                            }}
                          />
                        </div>
                        <Tooltip
                          target={subActiveText.value?.$el}
                          validate={subActiveText.value?.isEllipsisActive}
                          v-slots={{
                            content: () => subActiveOptions.value.map(({ option: o }) => o.label || o.name).join(', '),
                          }}
                        />
                      </>
                        }
                      </div>
                  }
                  {
                    optionSuffixSlot
                      ? optionSuffixSlot(
                        {
                          option: props,
                          markText,
                          active: active.value,
                          subActive: subActive.value,
                          subOptions: subOptions.value,
                          disabled: props.disabled,
                          pressing: mouseState.pressing,
                          hover: mouseState.hover,
                        },
                      )
                      : (
                        !isLeaf.value && <Icon
                          color={props.disabled ? 'text-disabled' : 'text-description'}
                          icon={Right}
                        />
                      )
                  }
                </Grid>
              </GridRow>
          ),
          option: optionSlot,
          optionPrefix: optionPrefixSlot,
          optionContent: optionContentSlot,
          optionSuffix: optionSuffixSlot,
          options: slots.default,
          stickTop: slots.stickTop,
          top: slots.top,
          bottom: slots.bottom,
          stickBottom: slots.stickBottom,
        }}
      />
    }
  },
})
